/**
 * Page Registry — Maps URL path segments to lazy-loaded page components.
 *
 * Used by the /_protected/$ catch-all route to resolve pages
 * without individual route files.
 */
import { lazy, type ComponentType } from "react";

type LazyPage = ComponentType;

/* ─── Static page map: pagePath → lazy import ─── */

const PAGE_MAP: Record<string, () => Promise<{ default: LazyPage }>> = {
  // Dashboard & Top-level
  "": () => imp("dashboard/index", "DashboardPage"),
  dashboard: () => imp("dashboard/index", "DashboardPage"),
  "member/mypage": () => imp("member/mypage", "MemberMyPage"),
  "member/checkout": () => imp("member/checkout", "MemberCheckoutPage"),
  "member/checkout/callback": () =>
    imp("member/checkout-callback", "MemberCheckoutCallbackPage"),

  // ═══ 키워드 (검색키워드 + 검색결과 통합) ═══
  keywords: () => imp("keywords/overview/index", "KeywordsOverviewPage"),
  "keywords/analysis": () =>
    imp("keywords/analysis/index", "KeywordsAnalysisPage"),
  "keywords/analysis/clusters": () =>
    imp("keywords/analysis/clusters-page/index", "KeywordClustersPage"),
  "keywords/analysis/gap": () =>
    imp("keywords/analysis/gap-page/layout", "KeywordsAnalysisGapPage"),
  "keywords/analysis/intent": () =>
    imp("keywords/analysis/intent-page/index", "KeywordsAnalysisIntentPage"),
  "keywords/data": () =>
    imp("keywords/data-overview/index", "KeywordsDataPage"),
  "keywords/data/trends": () =>
    imp("keywords/data/trends", "KeywordsDataTrendsPage"),
  "keywords/data/stats": () =>
    imp("keywords/data/stats", "KeywordsDataStatsPage"),
  "keywords/data/rankings": () =>
    imp("keywords/data/rankings", "KeywordsDataRankingsPage"),
  "keywords/explorer/list": () =>
    imp("keywords/explorer/list", "KeywordsExplorerListPage"),
  "keywords/tracking": () =>
    imp("keywords/tracking/index", "KeywordsTrackingPage"),
  "keywords/tracking/positions": () =>
    imp("keywords/tracking/positions", "KeywordsTrackingPositionsPage"),
  "keywords/tracking/alerts": () =>
    imp("keywords/tracking/alerts", "KeywordsTrackingAlertsPage"),
  // SERP (기존 search-results/analysis 페이지 재사용)
  "keywords/serp": () =>
    imp("search-results/analysis/index", "SearchResultsAnalysisPage"),
  "keywords/serp/layout": () =>
    imp(
      "search-results/analysis/serp-layout-page/index",
      "SearchResultsSerpLayoutPage",
    ),
  "keywords/serp/benchmark": () =>
    imp(
      "search-results/analysis/benchmark-page/index",
      "SearchResultsBenchmarkPage",
    ),
  "keywords/serp/suggestions": () =>
    imp(
      "search-results/analysis/suggestions-page/index",
      "SearchResultsSuggestionsPage",
    ),

  // ═══ 콘텐츠 (콘텐츠 + 채널 통합) ═══
  contents: () => imp("contents/index", "ContentsOverviewPage"),
  "contents/analysis": () =>
    imp("contents/analysis/index", "ContentsAnalysisPage"),
  "contents/analysis/summary": () =>
    imp("contents/analysis/summary", "ContentSummaryPage"),
  "contents/analysis/trend-match": () =>
    imp(
      "contents/analysis/trend-match-page/layout",
      "ContentsAnalysisTrendMatchPage",
    ),
  "contents/workspace": () =>
    imp("contents/workspace/index", "ContentsWorkspacePage"),
  "contents/workspace/write-post": () =>
    imp("contents/workspace/write-post", "ContentsWorkspaceWritePostPage"),
  "contents/workspace/brand-voice": () =>
    imp("contents/workspace/brand-voice", "ContentsWorkspaceBrandVoicePage"),
  "contents/workspace/calendar": () =>
    imp("contents/workspace/calendar", "ContentsWorkspaceCalendarPage"),
  "contents/workspace/draft": () =>
    imp(
      "contents/workspace/draft-simulate/index",
      "ContentsWorkspaceDraftSimulatePage",
    ),
  "contents/workspace/draft/review": () =>
    imp(
      "contents/workspace/draft-review/index",
      "ContentsWorkspaceDraftReviewPage",
    ),
  // 검색 노출 점검 (기존 contents/analysis 페이지 재사용)
  "contents/workspace/optimize": () =>
    imp(
      "contents/analysis/optimize-page/layout",
      "ContentsAnalysisOptimizePage",
    ),
  // ═══ 콘텐츠 탐색기 ═══
  "contents/explorer": () =>
    imp("contents/explorer/index", "ContentsExplorerListPage"),

  // ═══ 채널 ═══
  "channels/diagnostics": () =>
    imp("channels/diagnostics/index", "ChannelsDiagnosticsPage"),
  "channels/diagnostics/health": () =>
    imp(
      "channels/diagnostics/health-page/index",
      "ChannelsDiagnosticsHealthPage",
    ),
  "channels/diagnostics/compare": () =>
    imp(
      "channels/diagnostics/compare-page/index",
      "ChannelsDiagnosticsComparePage",
    ),
  "channels/diagnostics/forecast": () =>
    imp("channels/diagnostics/forecast", "ChannelsDiagnosticsForecastPage"),
  "channels/diagnostics/consulting": () =>
    imp(
      "channels/workspace/consulting-page/index",
      "ChannelsWorkspaceConsultingPage",
    ),
  "channels/proposals": () =>
    imp(
      "channels/workspace/proposals-page/index",
      "ChannelsWorkspaceProposalsPage",
    ),

  // ═══ 검색광고 ═══
  "search-marketing": () =>
    imp("search-marketing/overview/index", "SearchMarketingOverviewPage"),
  "search-marketing/keywords/explorer": () =>
    imp(
      "search-marketing/keywords/explorer",
      "SearchMarketingKeywordsExplorerPage",
    ),
  "search-marketing/workspace/copy-generator": () =>
    imp(
      "search-marketing/workspace/copy-generator-page/index",
      "SearchMarketingCopyGeneratorPage",
    ),
  "search-marketing/optimization": () =>
    imp(
      "search-marketing/optimization/index",
      "SearchMarketingOptimizationPage",
    ),
  "search-marketing/optimization/roi-simulator": () =>
    imp(
      "search-marketing/optimization/roi-simulator",
      "SearchMarketingRoiSimulatorPage",
    ),
  "search-marketing/optimization/bid-strategy": () =>
    imp(
      "search-marketing/optimization/bid-strategy-page/index",
      "SearchMarketingBidStrategyPage",
    ),
  "search-marketing/campaigns/builder": () =>
    imp(
      "search-marketing/campaigns/builder",
      "SearchMarketingCampaignBuilderPage",
    ),

  // ═══ 인사이트 (기존 monitoring/ 페이지 재사용) ═══
  insights: () => imp("monitoring/index", "MonitoringOverviewPage"),
  "insights/anomalies": () =>
    imp("monitoring/anomalies/index", "MonitoringAnomaliesPage"),
  "insights/anomalies/detection": () =>
    imp(
      "monitoring/anomalies/detection-page/index",
      "MonitoringAnomalyDetectionPage",
    ),
  "insights/anomalies/competitors": () =>
    imp("monitoring/anomalies/competitors", "MonitoringCompetitorsPage"),
  "insights/quality/low-quality": () =>
    imp("monitoring/quality/low-quality", "MonitoringLowQualityPage"),

  // ═══ 채널 탐색기 ═══
  "channels/explorer": () =>
    imp("channels/explorer/index", "ChannelsExplorerListPage"),
  // ═══ 검색결과 탐색기 ═══
  "keywords/serp/explorer": () =>
    imp("search-results/explorer/index", "SearchResultsExplorerAllPage"),
};

/* ─── Dynamic routes: pattern → loader with param extraction ─── */

interface DynamicRoute {
  pattern: RegExp;
  paramNames: string[];
  load: (
    params: Record<string, string>,
  ) => () => Promise<{ default: LazyPage }>;
}

const DYNAMIC_ROUTES: DynamicRoute[] = [
  {
    pattern: /^keywords\/details\/(.+)$/,
    paramNames: ["id"],
    load: () => () => imp("keywords/details/$id", "KeywordDetailPage"),
  },
];

/* ─── Helper: named re-export wrapper for lazy() ─── */

function imp(path: string, exportName: string): Promise<{ default: LazyPage }> {
  // Vite requires static string patterns in import() for bundling.
  // We use a glob import to cover all shared/pages.
  const modules = import.meta.glob<{ [key: string]: LazyPage }>(
    "../pages/**/*.tsx",
  );
  const key = `../pages/${path}.tsx`;
  const loader = modules[key];
  if (!loader) {
    return Promise.resolve({
      default: () => {
        throw new Error(`Page not found: ${path}`);
      },
    }) as Promise<{ default: LazyPage }>;
  }
  return loader().then((mod) => ({
    default: mod[exportName] ?? mod.default ?? (() => null),
  }));
}

/* ─── Public API ─── */

export interface ResolvedPage {
  Component: LazyPage;
  params: Record<string, string>;
}

export function resolvePage(splat: string): ResolvedPage {
  // Normalize: strip trailing slash
  const normalized = splat.replace(/\/+$/, "");

  // 1. Direct match
  const directLoader = PAGE_MAP[normalized];
  if (directLoader) {
    return {
      Component: lazy(directLoader),
      params: {},
    };
  }

  // 2. Dynamic pattern match
  for (const route of DYNAMIC_ROUTES) {
    const match = normalized.match(route.pattern);
    if (match) {
      const params: Record<string, string> = {};
      route.paramNames.forEach((name, i) => {
        params[name] = match[i + 1]!;
      });
      return {
        Component: lazy(route.load(params)),
        params,
      };
    }
  }

  // 3. Fallback — 404
  return {
    Component: lazy(() =>
      Promise.resolve({
        default: (() => null) as unknown as LazyPage,
      }),
    ),
    params: {},
  };
}
